import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useMemo } from "react";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { usePlayer } from "@/context/PlayerContext";
import { LogoMark } from "@/components/Logo";
import DesktopNav from "@/components/header/DesktopNav";
import MobileNav from "@/components/header/MobileNav";
import UserMenu from "@/components/header/UserMenu";
import OnAirIndicator from "@/components/header/OnAirIndicator";

const BASE_NAV = [
  { to: "/", label: "Radio", key: "radio" },
  { to: "/browse", label: "Browse", key: "browse" },
  { to: "/merch", label: "Merch", key: "merch" },
  { to: "/promotions", label: "Promo", key: "promo" },
  { to: "/chat", label: "Chat", key: "chat" },
  { to: "/manual", label: "Manual", key: "manual" },
];

/**
 * Top-of-page navigation. Composes into small subcomponents (DesktopNav /
 * MobileNav / UserMenu / OnAirIndicator) so each piece stays testable and
 * this file stays about layout, not logic.
 */
export default function Header() {
  const { user, logout } = useAuth();
  const { current, playing } = usePlayer();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Studio + Admin links only appear once the user is signed in.
  const navItems = useMemo(() => {
    const items = [...BASE_NAV];
    if (user) items.push({ to: "/dashboard", label: "Studio", key: "dashboard" });
    if (user?.role === "admin") items.push({ to: "/admin", label: "Admin", key: "admin", emphasize: true });
    return items;
  }, [user]);

  const isActive = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-white/10">
      <div className="h-[2px] w-full bg-gradient-to-r from-[#FF4500] via-[#FF4500]/40 to-transparent" />

      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-4 md:px-8 h-16 md:h-[72px]">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <LogoMark size={38} className="transition-transform group-hover:rotate-[-3deg]" />
          <div className="hidden sm:flex flex-col leading-none">
            <span className="font-display text-xl md:text-2xl font-black tracking-tighter">
              Melo<span className="text-[#FF4500]">Craft</span>
            </span>
            <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-gray-500 mt-1">
              The Artist-First Market
            </span>
          </div>
        </Link>

        <DesktopNav items={navItems} />

        <div className="flex items-center gap-2">
          <OnAirIndicator live={!!(current && playing)} />
          <UserMenu user={user} onLogout={handleLogout} />

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden h-9 w-9 grid place-items-center border border-[#2A2A2A] bg-black/60 text-white ml-1"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <MobileNav
        items={navItems}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        activeCheck={isActive}
      />
    </header>
  );
}
